import * as React from 'react';

import {
  enumReportingUploadType,
  type GovernmentReportingServiceType,
  type ReportingUploadType,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  ConsoleDialog,
  Row,
  Select,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { LucideFileBarChart } from 'lucide-react';

import * as govReportingState from '../../_state/govReporting.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FormSection } from '../_shared/FormSection';

import { DaysOfWeekPicker } from './DaysOfWeekPicker';
import { getServiceTypeOptions } from './serviceTypeOptions';

const UPLOAD_TYPE_KEYS = Object.values(enumReportingUploadType);

export const GovReportingConfigFormDialog = () => {
  const t = useTranslations();
  const [name, setName] = React.useState('');
  const [serviceType, setServiceType] = React.useState('');
  const [uploadType, setUploadType] = React.useState<string>(
    enumReportingUploadType.Automatic,
  );
  const [recyclerId, setRecyclerId] = React.useState('');
  const [toEmailAddress, setToEmailAddress] = React.useState('');
  const [uploadTimezone, setUploadTimezone] = React.useState('');
  const [encryptedCredentials, setEncryptedCredentials] = React.useState('');
  const [daysOfWeek, setDaysOfWeek] = React.useState<Record<string, boolean>>(
    {},
  );
  const [processBatchesAsUnit, setProcessBatchesAsUnit] = React.useState(false);
  const [isActive, setIsActive] = React.useState(true);
  const editingConfigId = useAtomValue(
    govReportingState.$editingGovReportingConfig,
  );
  const configs = useAtomValue(govReportingState.$rawGovReportingConfigs);
  const [isOpen, setIsOpen] = useAtom(
    govReportingState.$isGovReportingDialogOpen,
  );
  const [{ isPending: isCreating }, createConfig] = useAtom(
    govReportingState.$createGovReportingConfig,
  );
  const [{ isPending: isUpdating }, updateConfig] = useAtom(
    govReportingState.$updateGovReportingConfig,
  );
  const toast = useToast();

  const editingConfig = React.useMemo(
    () => configs.data?.find((c) => c.id === editingConfigId),
    [configs.data, editingConfigId],
  );
  const isEditing = !!editingConfig;
  const isPending = isCreating || isUpdating;

  const serviceTypeOptions = React.useMemo(
    () => getServiceTypeOptions(t.complianceView.serviceTypeLabels),
    [t],
  );

  React.useEffect(() => {
    if (isOpen && editingConfig) {
      setName(editingConfig.name);
      setServiceType(editingConfig.serviceType);
      setUploadType(editingConfig.uploadType);
      setRecyclerId(editingConfig.recyclerId ?? '');
      setToEmailAddress(editingConfig.toEmailAddress ?? '');
      setUploadTimezone(editingConfig.uploadTimezone ?? '');
      setEncryptedCredentials('');
      setDaysOfWeek(
        (editingConfig.daysOfWeek as Record<string, boolean> | null) ?? {},
      );
      setProcessBatchesAsUnit(editingConfig.processBatchesAsUnit);
      setIsActive(editingConfig.isActive);
    } else if (isOpen) {
      setName('');
      setServiceType('');
      setUploadType(enumReportingUploadType.Automatic);
      setRecyclerId('');
      setToEmailAddress('');
      setUploadTimezone('');
      setEncryptedCredentials('');
      setDaysOfWeek({});
      setProcessBatchesAsUnit(false);
      setIsActive(true);
    }
  }, [isOpen, editingConfig]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    try {
      if (isEditing && editingConfig) {
        await updateConfig({
          id: editingConfig.id,
          name,
          serviceType: serviceType as GovernmentReportingServiceType,
          uploadType: uploadType as ReportingUploadType,
          recyclerId: recyclerId || undefined,
          toEmailAddress: toEmailAddress || undefined,
          uploadTimezone: uploadTimezone || undefined,
          encryptedCredentials: encryptedCredentials || undefined,
          daysOfWeek:
            Object.keys(daysOfWeek).length > 0 ? daysOfWeek : undefined,
          processBatchesAsUnit,
          isActive,
        });
        toast.success(
          t.complianceView.govReporting.dialog.messages.updateSuccess,
        );
      } else {
        if (!name || !serviceType) return;
        await createConfig({
          name,
          serviceType: serviceType as GovernmentReportingServiceType,
          uploadType: uploadType as ReportingUploadType,
          recyclerId: recyclerId || undefined,
          toEmailAddress: toEmailAddress || undefined,
          uploadTimezone: uploadTimezone || undefined,
          encryptedCredentials: encryptedCredentials || undefined,
          daysOfWeek:
            Object.keys(daysOfWeek).length > 0 ? daysOfWeek : undefined,
          processBatchesAsUnit,
          isActive,
        });
        toast.success(
          t.complianceView.govReporting.dialog.messages.createSuccess,
        );
      }
      handleClose();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isEditing) {
        toast.error(
          t.complianceView.govReporting.dialog.messages.updateError(msg),
        );
      } else {
        toast.error(
          t.complianceView.govReporting.dialog.messages.createError(msg),
        );
      }
    }
  };

  const canSubmit = !!name && !!serviceType;

  return (
    <ConsoleDialog
      className="@GovReportingConfigFormDialog"
      title={
        isEditing
          ? t.complianceView.govReporting.dialog.editTitle
          : t.complianceView.govReporting.dialog.createTitle
      }
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      isLoading={isPending}
      askBeforeClosing={name !== '' || serviceType !== ''}
      content={
        <Col gap6>
          <FormSection
            icon={LucideFileBarChart}
            title={t.complianceView.govReporting.dialog.sections.configuration}
          >
            <Col gap4>
              <TextField
                label={t.complianceView.govReporting.dialog.fields.name}
                value={name}
                onChange={setName}
                placeholder={
                  t.complianceView.govReporting.dialog.placeholders.name
                }
                isRequired
              />
              <div className="grid grid-cols-2 gap-4">
                <Select
                  label={
                    t.complianceView.govReporting.dialog.fields.serviceType
                  }
                  value={serviceType}
                  onChange={setServiceType}
                  options={serviceTypeOptions}
                  placeholder={
                    t.complianceView.govReporting.dialog.placeholders
                      .serviceType
                  }
                  isRequired
                />
                <Select
                  label={t.complianceView.govReporting.dialog.fields.uploadType}
                  value={uploadType}
                  onChange={setUploadType}
                  options={UPLOAD_TYPE_KEYS.map((key) => ({
                    key,
                    label:
                      t.complianceView.govReporting.dialog.uploadTypes[key] ??
                      key,
                  }))}
                  placeholder={
                    t.complianceView.govReporting.dialog.placeholders.uploadType
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <TextField
                  label={t.complianceView.govReporting.dialog.fields.recyclerId}
                  value={recyclerId}
                  onChange={setRecyclerId}
                  placeholder={
                    t.complianceView.govReporting.dialog.placeholders.recyclerId
                  }
                />
                <TextField
                  label={
                    t.complianceView.govReporting.dialog.fields.toEmailAddress
                  }
                  value={toEmailAddress}
                  onChange={setToEmailAddress}
                  placeholder={
                    t.complianceView.govReporting.dialog.placeholders
                      .toEmailAddress
                  }
                />
              </div>
              <TextField
                label={
                  t.complianceView.govReporting.dialog.fields.uploadTimezone
                }
                value={uploadTimezone}
                onChange={setUploadTimezone}
                placeholder={
                  t.complianceView.govReporting.dialog.placeholders
                    .uploadTimezone
                }
              />
              <TextField
                label={
                  t.complianceView.govReporting.dialog.fields
                    .encryptedCredentials
                }
                value={encryptedCredentials}
                onChange={setEncryptedCredentials}
                placeholder={
                  t.complianceView.govReporting.dialog.placeholders
                    .encryptedCredentials
                }
                type="password"
              />
              <Col gap2>
                <div className="text-sm font-medium">
                  {t.complianceView.govReporting.dialog.fields.daysOfWeek}
                </div>
                <DaysOfWeekPicker
                  value={daysOfWeek}
                  onChange={setDaysOfWeek}
                  dayLabels={t.complianceView.govReporting.detail.dayLabels}
                />
              </Col>
              <Row className="items-center gap-3">
                <Switch
                  isSelected={processBatchesAsUnit}
                  onChange={setProcessBatchesAsUnit}
                />
                <span className="text-sm">
                  {
                    t.complianceView.govReporting.dialog.fields
                      .processBatchesAsUnit
                  }
                </span>
              </Row>
              <Row className="items-center gap-3">
                <Switch isSelected={isActive} onChange={setIsActive} />
                <span className="text-sm">
                  {t.complianceView.govReporting.dialog.fields.isActive}
                </span>
              </Row>
            </Col>
          </FormSection>
        </Col>
      }
      footer={
        <Button
          color="primary"
          variant="solid"
          isDisabled={!canSubmit}
          isPending={isPending}
          isLoading={isPending}
          onPress={handleSubmit}
        >
          {isEditing
            ? t.complianceView.govReporting.dialog.actions.save
            : t.complianceView.govReporting.dialog.actions.create}
        </Button>
      }
    />
  );
};
